import { confirmCompanyForm } from '@viilutalous/common';
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Business, GroupAdd } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { api } from '@viilutalous/common';
import { useAuth } from '../modules/auth';

export default function TenantRegistration({ onSuccess, userEmail }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [mode, setMode] = useState(null); // 'create' or 'request'
  const [companyName, setCompanyName] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [coaType, setCoaType] = useState('real_estate');
  const [tenantId, setTenantId] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleCreateTenant = async () => {
    if (!companyName.trim()) {
      setError(t('registration.company_name_required'));
      return;
    }
    if (!businessId.trim()) {
      setError(t('registration.business_id_required'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await confirmCompanyForm((payload) => api.post('/auth/register', payload), {
        company_name: companyName.trim(),
        business_id: businessId.trim(),
        coa_type: coaType,
        terms_accepted: true,
      }, t);
      if (!response) return;
      setSuccess(response.data.message || t('registration.tenant_created'));
      // Wait a moment then refresh
      setTimeout(() => {
        onSuccess?.();
      }, 1500);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('registration.create_error');
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async () => {
    if (!tenantId.trim()) {
      setError(t('registration.tenant_id_required'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/auth/request-access', {
        tenant_id: tenantId.trim(),
        message: message || undefined,
        terms_accepted: true,
      });
      setSuccess(response.data.message || t('registration.request_sent'));
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('registration.request_error');
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  // Initial selection screen
  if (!mode) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom align="center">
              {t('registration.welcome')}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
              {userEmail}
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
              {t('registration.no_tenant_access')}
            </Typography>

            <Stack spacing={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Business />}
                onClick={() => setMode('create')}
                fullWidth
              >
                {t('registration.create_new_tenant')}
              </Button>

              <Divider>{t('registration.or')}</Divider>

              <Button
                variant="outlined"
                size="large"
                startIcon={<GroupAdd />}
                onClick={() => setMode('request')}
                fullWidth
              >
                {t('registration.request_access')}
              </Button>
            </Stack>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button color="inherit" onClick={handleLogout}>
                {t('registration.logout')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Create tenant form
  if (mode === 'create') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              {t('registration.create_tenant_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('registration.create_tenant_description')}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              fullWidth
              label={t('registration.company_name')}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={t('registration.company_name_placeholder')}
              sx={{ mb: 2 }}
              disabled={loading || !!success}
              required
            />

            <TextField
              fullWidth
              label={t('registration.business_id')}
              value={businessId}
              onChange={(e) => setBusinessId(e.target.value)}
              placeholder={t('registration.business_id_placeholder')}
              sx={{ mb: 2 }}
              disabled={loading || !!success}
              required
            />

            <FormControl fullWidth sx={{ mb: 3 }} disabled={loading || !!success}>
              <InputLabel id="coa-type-label">{t('registration.coa_type')}</InputLabel>
              <Select
                labelId="coa-type-label"
                value={coaType}
                label={t('registration.coa_type')}
                onChange={(e) => setCoaType(e.target.value)}
              >
                <MenuItem value="real_estate">{t('registration.coa_real_estate')}</MenuItem>
                <MenuItem value="business">{t('registration.coa_business')}</MenuItem>
                <MenuItem value="association">{t('registration.coa_association')}</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setMode(null)}
                disabled={loading}
              >
                {t('common.back')}
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateTenant}
                disabled={loading || !!success}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : t('registration.create_tenant_button')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Request access form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            {t('registration.request_access_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('registration.request_access_description')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            label={t('registration.tenant_id')}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            sx={{ mb: 2 }}
            disabled={loading || !!success}
            required
          />

          <TextField
            fullWidth
            label={t('registration.message_optional')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('registration.message_placeholder')}
            multiline
            rows={3}
            sx={{ mb: 3 }}
            disabled={loading || !!success}
          />

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setMode(null)}
              disabled={loading}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleRequestAccess}
              disabled={loading || !!success}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : t('registration.send_request')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
